/**
 * V2 Leak Teaser — private client-facing view at /leak/{token}
 *
 * On-brand dark V2. Token-only auth. NOINDEX, NOFOLLOW, NOARCHIVE. Not
 * in the sitemap. Populated from `GET /api/leak/{token}` which strips
 * everything internal and scrubs any surviving numbers.
 *
 * The whole page's job: "if we spotted this in 10 minutes, imagine what
 * the paid work uncovers." Sells the pain + capability. Never the value
 * of the fix. Never a dollar figure.
 */
/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const C = {
  navy:    '#0b0726',
  surface: '#1A0F44',
  hair:    'rgba(220,240,245,0.14)',
  icy:     '#EFF0FF',
  muted:   'rgba(239,240,255,0.85)',
  faint:   'rgba(239,240,255,0.62)',   // was 0.55 — bumped one step
  teal:    '#34D1B0',
  magenta: '#EB258D',
};

const LEAK_COLOR = {
  time:        '#EB258D',
  money:       '#9156C4',
  lost_work:   '#4F8FD0',
  pricing:     '#1FB6C1',
  blind_spots: '#34D1B0',
};

const LEAK_LABEL = {
  time: 'Time', money: 'Money', lost_work: 'Lost work',
  pricing: 'Pricing', blind_spots: 'Blind spots',
};

// SpectrumWord — MUST have a solid-colour fallback (#EFF0FF) via `color`.
// Some Chromium/WebKit paths lose background-clip on secondary paint, and
// dark-mode auto-inversion has been known to hide gradient-clipped text
// entirely. Explicit solid colour keeps the word legible even if the
// gradient fails to apply.
const SpectrumWord = ({ children }) => (
  <span style={{
    color: '#EFF0FF', // solid fallback — never invisible
    backgroundImage: 'linear-gradient(95deg,#34D1B0 0%,#1FB6C1 25%,#4F8FD0 50%,#9156C4 75%,#EB258D 100%)',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  }}>{children}</span>
);

// Format the booking time in the INVITEE's timezone (not the server's).
const fmtBooking = (iso, tz) => {
  if (!iso) return '';
  try {
    const dt = new Date(iso);
    const opts = { weekday: 'long', day: 'numeric', month: 'long',
                    hour: 'numeric', minute: '2-digit', hour12: true,
                    timeZone: tz || 'Australia/Brisbane',
                    timeZoneName: 'short' };
    return new Intl.DateTimeFormat('en-AU', opts).format(dt);
  } catch (e) {
    return '';
  }
};

export default function V2LeakTeaserPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    // Force noindex on this route regardless of DocumentHead's route map.
    let m = document.querySelector('meta[name="robots"]');
    if (!m) {
      m = document.createElement('meta');
      m.setAttribute('name', 'robots');
      document.head.appendChild(m);
    }
    m.setAttribute('content', 'noindex, nofollow, noarchive');
    document.title = 'Kindra — private leak preview';
    axios.get(`${API}/leak/${encodeURIComponent(token || '')}`)
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.detail || 'not_found'));
  }, [token]);
  const bookingLine = useMemo(() => {
    if (!data) return '';
    return fmtBooking(data.booking_time_iso, data.invitee_tz);
  }, [data]);

  if (err) {
    return (
      <div style={{ background: C.navy, minHeight: '100vh', color: C.icy, padding: '48px 24px' }}>
        <div style={{ maxWidth: 640, margin: '80px auto', textAlign: 'center' }} data-testid="teaser-not-found">
          <p style={{ fontFamily: 'Inter', color: C.faint, fontSize: 12, letterSpacing: '0.24em', textTransform: 'uppercase' }}>
            Kindra
          </p>
          <h1 style={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: 28, marginTop: 14 }}>
            This link is no longer valid.
          </h1>
          <p style={{ fontFamily: 'Inter', color: C.muted, fontSize: 15, marginTop: 12 }}>
            If Michael sent this to you, please reply to his email and he'll send a fresh link.
          </p>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div style={{ background: C.navy, minHeight: '100vh', color: C.faint, fontFamily: 'Inter', padding: 48 }}>Loading…</div>;
  }

  const firstName = (data.first_name || '').trim();
  const businessName = data.business_name;

  return (
    <div style={{ background: C.navy, minHeight: '100vh', color: C.icy }} data-testid="teaser-page">
      {/* Simple logo header */}
      <header style={{ padding: '28px 24px 12px' }}>
        <div style={{ maxWidth: 860, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontFamily: 'Poppins', fontWeight: 700, fontSize: 22 }}>
            <span style={{ display: 'inline-block', width: 26, height: 26, borderRadius: 8,
                            background: 'linear-gradient(95deg,#34D1B0,#1FB6C1,#4F8FD0,#9156C4,#EB258D)',
                            marginRight: 10, verticalAlign: 'middle' }} />
            <span style={{ verticalAlign: 'middle' }}>Kindra <span style={{ background: 'linear-gradient(95deg,#34D1B0,#EB258D)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>AI</span></span>
          </div>
          <p style={{ fontFamily: 'Inter', color: C.faint, fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase' }} data-testid="teaser-private-mark">
            Private preview
          </p>
        </div>
      </header>

      {/* Hero */}
      <section style={{ padding: '32px 24px 24px' }}>
        <div style={{ maxWidth: 780, margin: '0 auto' }}>
          <p style={{ fontFamily: 'Inter', color: C.faint, fontSize: 12, letterSpacing: '0.24em', textTransform: 'uppercase', marginBottom: 14 }}
              data-testid="teaser-eyebrow">
            For {businessName}
          </p>
          <h1 style={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: 'clamp(30px,4.5vw,44px)', lineHeight: 1.14, marginBottom: 18, color: C.icy }}
              data-testid="teaser-h1">
            Look at just some of what we spotted<br />in a <SpectrumWord>10-minute chat</SpectrumWord>.
          </h1>
          <p style={{ fontFamily: 'Inter', fontSize: 17, lineHeight: 1.6, color: C.muted, marginBottom: 8 }}
              data-testid="teaser-intro">
            {firstName ? `G'day ${firstName} — thanks for the chat with Kindra. `
                       : 'Thanks for the chat with Kindra. '}
            Here's a taste before our call — no jargon, no sales pitch.
          </p>
        </div>
      </section>

      {/* Privacy lock */}
      <section style={{ padding: '8px 24px 24px' }}>
        <div style={{ maxWidth: 780, margin: '0 auto',
                        background: 'linear-gradient(120deg, rgba(52,209,176,0.08), rgba(31,182,193,0.08))',
                        border: `1px solid ${C.hair}`, borderLeft: `3px solid ${C.teal}`,
                        borderRadius: 12, padding: '16px 22px' }}
              data-testid="teaser-privacy-lock">
          <p style={{ fontFamily: 'Inter', fontSize: 11, letterSpacing: '0.14em', color: C.teal, fontWeight: 700, marginBottom: 8 }}>
            🔒 PRIVATE TO YOUR BUSINESS
          </p>
          <p style={{ fontFamily: 'Inter', fontSize: 15, lineHeight: 1.6, color: C.icy }}>
            This page is private to you. The numbers and the full plan — including what fixing each one is worth — we'll walk through together on our call.
          </p>
        </div>
      </section>

      {/* Journey / leak map */}
      {(data.stages || []).length > 0 && (
        <section style={{ padding: '24px' }} data-testid="teaser-journey">
          <div style={{ maxWidth: 780, margin: '0 auto' }}>
            <h2 style={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: 22, marginBottom: 14, color: C.icy }}>Your day-to-day flow</h2>
            <p style={{ fontFamily: 'Inter', color: C.faint, fontSize: 13, marginBottom: 18 }}>
              <span style={{ display:'inline-block', width:10, height:10, borderRadius:5, background: C.teal, marginRight: 6, verticalAlign:'middle' }} />
              Running well
              <span style={{ margin: '0 14px', color: C.hair }}>·</span>
              <span style={{ display:'inline-block', width:10, height:10, borderRadius:5, background: C.magenta, marginRight: 6, verticalAlign:'middle' }} />
              Leaking
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
              {data.stages.map((s, i) => {
                const isLeak = s.state === 'leaking';
                const col = isLeak ? C.magenta : C.teal;
                return (
                  <div key={i}
                        data-testid={`teaser-stage-${i}`}
                        style={{ background: `${col}10`, border: `1px solid ${col}55`,
                                  borderLeft: `3px solid ${col}`, borderRadius: 10,
                                  padding: '12px 14px' }}>
                    <p style={{ fontFamily: 'Inter', fontSize: 10, letterSpacing: '0.14em',
                                  textTransform: 'uppercase', color: col, fontWeight: 700 }}>
                      {isLeak ? 'Leaking' : 'Clean'}
                    </p>
                    <p style={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: 15, color: C.icy, marginTop: 4 }}>
                      {s.label}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Per-leak cards */}
      <section style={{ padding: '32px 24px' }} data-testid="teaser-leaks">
        <div style={{ maxWidth: 780, margin: '0 auto' }}>
          <h2 style={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: 22, marginBottom: 20, color: C.icy }}>What we spotted</h2>
          {(data.leaks || []).map((l) => {
            const col = LEAK_COLOR[l.leak_area] || C.magenta;
            return (
              <article key={l.leak_area}
                        data-testid={`teaser-leak-${l.leak_area}`}
                        style={{ background: C.surface, border: `1px solid ${C.hair}`,
                                  borderLeft: `3px solid ${col}`, borderRadius: 12,
                                  padding: '18px 22px', marginBottom: 12 }}>
                <p style={{ fontFamily: 'Inter', fontSize: 11, letterSpacing: '0.16em',
                              textTransform: 'uppercase', color: col, fontWeight: 700, marginBottom: 8 }}>
                  {LEAK_LABEL[l.leak_area]}
                </p>
                <h3 style={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: 18, marginBottom: 10, color: C.icy }}>
                  {l.title}
                </h3>
                <p style={{ fontFamily: 'Inter', fontSize: 15, lineHeight: 1.6, color: C.muted, marginBottom: 12 }}>
                  {l.pain}
                </p>
                <div style={{ background: 'rgba(220,240,245,0.04)', border: `1px solid ${C.hair}`,
                                borderRadius: 8, padding: '10px 14px' }}>
                  <p style={{ fontFamily: 'Inter', fontSize: 11, letterSpacing: '0.14em', color: C.faint, fontWeight: 700, marginBottom: 4 }}>
                    WHAT WE'D BUILD
                  </p>
                  <p style={{ fontFamily: 'Inter', fontSize: 15, color: C.icy, lineHeight: 1.55 }}>{l.outcome}</p>
                  <p style={{ fontFamily: 'Inter', fontSize: 13, color: C.teal, marginTop: 8, fontStyle: 'italic' }}>
                    And you'd own it outright.
                  </p>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* CTA — already booked */}
      <section style={{ padding: '32px 24px 48px' }} data-testid="teaser-cta">
        <div style={{ maxWidth: 780, margin: '0 auto',
                        background: 'linear-gradient(120deg, rgba(235,37,141,0.10), rgba(31,182,193,0.10))',
                        border: `1px solid ${C.hair}`, borderRadius: 16, padding: '28px 32px' }}>
          <p style={{ fontFamily: 'Inter', color: C.faint, fontSize: 12, letterSpacing: '0.22em', textTransform: 'uppercase', marginBottom: 12 }}>
            This is just the surface
          </p>
          <h2 style={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: 'clamp(22px,3vw,28px)', lineHeight: 1.25, marginBottom: 14, color: C.icy }}>
            On our call Michael walks you through the full picture, what to tackle first, and what fixing it's worth.
          </h2>
          {bookingLine && (
            <div style={{ marginTop: 18, padding: '14px 18px', background: 'rgba(220,240,245,0.06)',
                            border: `1px solid ${C.hair}`, borderRadius: 10 }} data-testid="teaser-booking-time">
              <p style={{ fontFamily: 'Inter', fontSize: 11, letterSpacing: '0.14em', color: C.faint, fontWeight: 700, marginBottom: 4 }}>
                YOUR CALL
              </p>
              <p style={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: 17, color: C.icy }}>{bookingLine}</p>
            </div>
          )}
        </div>
      </section>

      <footer style={{ padding: '24px', borderTop: `1px solid ${C.hair}` }}>
        <p style={{ maxWidth: 780, margin: '0 auto', fontFamily: 'Inter', fontSize: 12, color: C.faint, textAlign: 'center' }}>
          Kindra AI · Find your leaks · Plug your leaks · private to your business.
        </p>
      </footer>
    </div>
  );
}
